// core modules
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AppRoutingModule } from './app-routing.module';
import { LOCALE_ID, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { DatePipe, registerLocaleData } from '@angular/common';
import localeIt from '@angular/common/locales/it';
import './prototypes/array-prototypes';
// additional modules
import { ToastrModule } from 'ngx-toastr';
import {
    NgxUiLoaderConfig,
    NgxUiLoaderModule,
    NgxUiLoaderRouterModule,
    PB_DIRECTION,
    POSITION,
    SPINNER
} from 'ngx-ui-loader';
import { NgSelectModule } from '@ng-select/ng-select';
import { NgxUploaderModule } from 'ngx-uploader';
import { NgPipesModule } from 'ngx-pipes';
import { ChartsModule } from 'ng2-charts';
// Services && interceptors
import { PTVMHttpInterceptor } from './services/http-interceptor.service';
import { AuthService } from './services/auth.service';
import { DataService } from './services/data.service';
import { WebSocketMessageService } from './services/web-socket/web-socket-message.service';
import { NotificationService } from './services/notification.service';
import { UtilsDateService } from './helpers/utils-date.service';
import { LocalStorageService } from './services/local-storage.service';
// Pipes & directives
import { RolePipe } from './pipes/role.pipe';
import { TopicPipe } from './pipes/topic.pipe';
import { ValidationDirective } from './directives/validation.directive';
import { RespectPatternDirective } from './directives/respectPattern.directive';
import { RespectLengthDirective } from './directives/respectLength.directive';
/** global components */
import { AppComponent } from './app.component';
import { LoginComponent } from './pages/common/login/login.component';
/** layout components */
import { HeaderComponent } from './components/header/header.component';
import { HeaderbarComponent } from './components/headerbar/headerbar.component';
import { SearchbarComponent } from './components/searchbar/searchbar.component';
import { SearchboxComponent } from './components/search-box/searchbox.component';
/** superadmin components */
import { SuperadminComponent } from './pages/role-specific/superadmin/superadmin.component';
import { SuperadminDashboardComponent } from './pages/role-specific/superadmin/dashboard/dashboard.component';
import { SuperadminUsersOperatorsComponent } from './pages/role-specific/superadmin/users/operators/users.operators.component';
import { SuperadminUsersAgenciesComponent } from './pages/role-specific/superadmin/users/agencies/users.agencies.component';
import { SuperadminUsersAgentsComponent } from './pages/role-specific/superadmin/users/agents/users.agents.component';
import { SuperadminRoutePermissionsComponent } from './pages/role-specific/superadmin/route-permissions/route-permissions.component';
/** admin components */
import { AdminComponent } from './pages/role-specific/admin/admin.component';
import { AdminDashboardComponent } from './pages/role-specific/admin/dashboard/dashboard.component';
import { AdminUsersAgenciesComponent } from './pages/role-specific/admin/users/agencies/users.agencies.component';
import { AdminUsersAgentsComponent } from './pages/role-specific/admin/users/agents/users.agents.component';
import { AdminSalesDocumentsComponent } from './pages/role-specific/admin/sales/documents/sales.documents.component';
import { AdminSidebarComponent } from './pages/role-specific/admin/sidebar/sidebar.component';
/** agency components */
import { AgencyComponent } from './pages/role-specific/agency/agency.component';
import { AgencyDashboardComponent } from './pages/role-specific/agency/dashboard/dashboard.component';
import { AgencySalesDocumentsComponent } from './pages/role-specific/agency/sales/documents/sales.documents.component';
import { AgencyStatisticsComponent } from './pages/role-specific/agency/statistics/statistics.component';
/** agent components */
import { AgentComponent } from './pages/role-specific/agent/agent.component';
import { AgentDashboardComponent } from './pages/role-specific/agent/dashboard/dashboard.component';
import { AgentSalesDocumentsComponent } from './pages/role-specific/agent/sales/documents/sales.documents.component';
import { SuperadminSidebarComponent } from './pages/role-specific/superadmin/sidebar/sidebar.component';
import { AgencySidebarComponent } from './pages/role-specific/agency/sidebar/sidebar.component';
import { AgentSidebarComponent } from './pages/role-specific/agent/sidebar/sidebar.component';
import { AgencyAgentsComponent } from './pages/role-specific/agency/users/agents/agents.component';
import { AgencyOperatorsComponent } from './pages/role-specific/agency/users/agency-operators/agency-operators.component';
import { FileDownloadService } from './services/file-download.service';
import { EmptyListComponent } from './components/empty-list/empty-list.component';
import { AutocompleteService } from './services/autocomplete.service';
import { ContractComponent } from './pages/common/contract/contract.component';
import { DocumentlistComponent } from './pages/common/documentlist/documentlist.component';
import { UserlistComponent } from './pages/common/userlist/userlist.component';
import { NgMultiSelectDropDownModule } from 'ng-multiselect-dropdown';
import { ProfileComponent } from './pages/common/profile/profile.component';
import { UtilsStringService } from './helpers/utils-string.service';
import { CustomerComponent } from './pages/common/customer/customer.component';
import { PasswordResetComponent } from './components/password-reset/password-reset.component';
import { ProgressBarModule } from 'angular-progress-bar';
import { PricelistComponent } from './pages/common/pricelist/pricelist.component';
import { GlobalsService } from './services/globals.service';
import { NgxLoadingModule } from 'ngx-loading';
import { HeaderService } from './services/header.service';
import { SessionStorageService } from './services/session-storage.service';
import { EtagService } from './services/etag.service';
import { BrandLogoComponent } from './components/brand-logo/brand-logo.component';
import { DatetimePickerComponent } from './components/datetime-picker/datetime-picker.component';
import { AudioPlayerComponent } from './components/audio-player/audio-player.component';
import { SuperadminSpecializedOperatorsComponent } from './pages/role-specific/superadmin/users/specialized-operators/superadmin-specialized-operators.component';
import { SpecializedOperatorComponent } from './pages/role-specific/specialized-operator/specialized-operator.component';
import { SpecializedOperatorSidebarComponent } from './pages/role-specific/specialized-operator/sidebar/specialized-operator-sidebar.component';
import { InternalEventsService } from './services/internal-events.service';
import { WebSocketSessionService } from './services/web-socket/web-socket-session.service';
import { ContractFromFileComponent } from './pages/common/contract-from-file/contract-from-file.component';
import { SuperadminUsersAgencyOperatorsComponent } from './pages/role-specific/superadmin/users/agency-operators/users.agency-operators.component';
import { AskForConfirmationComponent } from './components/ask-for-confirmation/ask-for-confirmation.component';
import { ContractService } from './services/resources/contract.service';
import { NgbActiveModal, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { NullIfEmptyDirective } from './directives/null-if-empty.directive';
import { SuperadminTemplatesComponent } from './pages/role-specific/superadmin/templates/templates.component';
import { SuperadminModalComponent } from './pages/role-specific/superadmin/templates/modal/modal.component';

registerLocaleData(localeIt);

const ngxUiLoaderConfig: NgxUiLoaderConfig = {
    bgsColor: '#dc3545',
    bgsOpacity: 6,
    bgsPosition: POSITION.centerCenter,
    bgsSize: 40,
    bgsType: SPINNER.threeStrings,
    fgsColor: '#dc3545',
    fgsType: SPINNER.threeStrings,
    pbColor: '#dc3545',
    pbDirection: PB_DIRECTION.leftToRight,
    pbThickness: 5,
    blur: 5,
    hasProgressBar: true
};

@NgModule({
    declarations: [
        AppComponent,
        LoginComponent,
        RolePipe,
        TopicPipe,
        ValidationDirective,
        RespectPatternDirective,
        RespectLengthDirective,
        SuperadminComponent,
        SuperadminDashboardComponent,
        SuperadminUsersOperatorsComponent,
        SuperadminUsersAgenciesComponent,
        SuperadminUsersAgentsComponent,
        SuperadminTemplatesComponent,
        SuperadminModalComponent,
        HeaderbarComponent,
        SearchbarComponent,
        HeaderComponent,
        AdminComponent,
        AdminDashboardComponent,
        AdminUsersAgenciesComponent,
        AdminUsersAgentsComponent,
        AdminSalesDocumentsComponent,
        AgencyComponent,
        AgencyDashboardComponent,
        AgencyAgentsComponent,
        AgencySalesDocumentsComponent,
        AgencyStatisticsComponent,
        AgentComponent,
        AgentDashboardComponent,
        AgentSalesDocumentsComponent,
        SuperadminRoutePermissionsComponent,
        SuperadminSidebarComponent,
        AdminSidebarComponent,
        AgencySidebarComponent,
        AgentSidebarComponent,
        AgencyAgentsComponent,
        AgencyOperatorsComponent,
        EmptyListComponent,
        ContractComponent,
        DocumentlistComponent,
        UserlistComponent,
        ProfileComponent,
        CustomerComponent,
        PasswordResetComponent,
        PricelistComponent,
        BrandLogoComponent,
        DatetimePickerComponent,
        AudioPlayerComponent,
        SuperadminSpecializedOperatorsComponent,
        SpecializedOperatorComponent,
        SpecializedOperatorSidebarComponent,
        ContractFromFileComponent,
        SuperadminUsersAgencyOperatorsComponent,
        AskForConfirmationComponent,
        NullIfEmptyDirective,
        SearchboxComponent
    ],
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        FormsModule,
        ReactiveFormsModule,
        HttpClientModule,
        AppRoutingModule,
        ToastrModule.forRoot(),
        NgbModule.forRoot(),
        NgxUiLoaderModule.forRoot(ngxUiLoaderConfig),
        NgxUiLoaderRouterModule,
        NgSelectModule,
        NgxUploaderModule,
        NgPipesModule,
        ChartsModule,
        NgMultiSelectDropDownModule.forRoot(),
        ProgressBarModule,
        NgxLoadingModule.forRoot({})
    ],
    entryComponents: [AskForConfirmationComponent, SuperadminModalComponent],
    providers: [
        {
            provide: HTTP_INTERCEPTORS,
            useClass: PTVMHttpInterceptor,
            multi: true
        },
        AuthService,
        DataService,
        WebSocketMessageService,
        WebSocketSessionService,
        NotificationService,
        FileDownloadService,
        DatePipe,
        UtilsDateService,
        UtilsStringService,
        AutocompleteService,
        { provide: LOCALE_ID, useValue: 'it' },
        GlobalsService,
        HeaderService,
        SessionStorageService,
        LocalStorageService,
        EtagService,
        InternalEventsService,
        NgbActiveModal,
        ContractService
    ],
    bootstrap: [AppComponent]
})
export class AppModule {}



// WEBPACK FOOTER //
// ./src/app/app.module.ts